import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';

/**
 * Coordinates hero and product slideshows inside the Curated Looks section.
 */
class CuratedLooksCarousel extends Component {
  requiredRefs = ['heroSlideshow'];

  /** @type {HTMLElement | undefined} */
  #activeGroup;

  /** @type {(event: SlideshowSelectEvent) => void} */
  #handleHeroChange = (event) => {
    if (!event) return;
    const heroIndex = typeof event.detail?.index === 'number' ? event.detail.index : 0;
    this.#activateGroup(heroIndex);
  };

  connectedCallback() {
    super.connectedCallback();

    if (!this.productGroups.length) return;

    const initialGroup = this.productGroups.find((group) => group.dataset.active === 'true') ?? this.productGroups[0];
    if (initialGroup) {
      this.#setActiveGroup(initialGroup);
    }

    this.heroSlideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleHeroChange);
  }

  disconnectedCallback() {
    this.heroSlideshow?.removeEventListener(SlideshowSelectEvent.eventName, this.#handleHeroChange);
    super.disconnectedCallback();
  }

  /**
   * @returns {HTMLElement[]}
   */
  get productGroups() {
    const { productGroups } = this.refs;
    if (!productGroups) return [];
    const groups = Array.isArray(productGroups) ? productGroups : [productGroups];
    return /** @type {HTMLElement[]} */ (groups.filter((group) => group instanceof HTMLElement));
  }

  /**
   * @returns {HTMLElement | undefined}
   */
  get heroSlideshow() {
    const { heroSlideshow } = this.refs;
    if (heroSlideshow instanceof HTMLElement) {
      return heroSlideshow;
    }

    return undefined;
  }

  /**
   * @param {number} heroIndex
   */
  #activateGroup(heroIndex) {
    const nextGroup = this.productGroups.find((group) => Number(group.dataset.heroIndex) === heroIndex) ?? this.productGroups[0];
    if (!nextGroup || nextGroup === this.#activeGroup) return;

    this.#setActiveGroup(nextGroup);
  }

  /**
   * @param {HTMLElement} group
   */
  #setActiveGroup(group) {
    const previous = this.#activeGroup;

    group.setAttribute('data-active', 'true');
    group.setAttribute('aria-hidden', 'false');

    if (previous && previous !== group) {
      previous.removeAttribute('data-active');
      previous.setAttribute('aria-hidden', 'true');
    }

    this.#activeGroup = group;
    this.#resetGroupSlideshow(group);
  }

  /**
   * @param {HTMLElement} group
   */
  #resetGroupSlideshow(group) {
    const slideshow = group.querySelector('slideshow-component');
    if (!(slideshow instanceof HTMLElement)) return;

    const slideshowComponent = /** @type {any} */ (slideshow);

    if (typeof slideshowComponent.select === 'function') {
      slideshowComponent.select(0, undefined, { animate: false });
      return;
    }

    slideshow.setAttribute('initial-slide', '0');
  }
}

if (!customElements.get('curated-looks-carousel')) {
  customElements.define('curated-looks-carousel', CuratedLooksCarousel);
}
